import { useState, useEffect, useCallback } from "react";
import BreadCrumbComp from "../../components/breadCrumb";
import SimpleSelect from "../../components/selects/simpleSelect";
import FormTextField from "../../components/textField/formTextField";
import LaddaButton from "../../components/buttons/laddaButton";
import ModalComponent from "../../components/modals";
import SimpleTable from "../../components/tables/simpleTable";
import * as yup from "yup";
import {
  probabilityData,
  impactData,
  controlFrequency,
  controlType,
  vulnerabilityData
} from "../../constants/selectData";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { icons } from "../../constants";
import { Alert, Grid } from "@mui/material";
import { getCookie } from "../../utils/getCookie";
import "./styles.css";
import { useLocation, useNavigate } from "react-router-dom";
import {
  callApiAsync,
  getCallProcess,
  getCallRiskType,
  getStrategiesSelect,
  postCreateRisk
} from "../../services/apiService";
import { filterProcessByRole } from "./authorizeFxs";
import { useSelector } from "react-redux";
import HeaderBanner from "../../components/headerBanner";
import { headerBannerType } from "../../constants/enums";
import StrategyRelationsTable from "../../components/StrategyRelationsTable";
import ModalComponentRiskHelp from "./modalRiskHelp";
import { riskHelpIds } from "./riskHelpData";
import { getRiskScore, getRiskLevelFromScore } from "./riskLevelLogic";
import { getCurrentMonthYear, getMonthName } from "../../utils/dateUtils";

const validationSchema = yup.object().shape({
  riskType: yup.string().required("Este campo es obligatorio"),
  riskTitle: yup.string().required("Este campo es obligatorio"),
  description: yup
    .string()
    .required("Este campo es obligatorio")
    .max(1500, "La descripción no puede exceder los 1500 caracteres"),
  impactProcess: yup.string().required("Este campo es obligatorio"),
  causes: yup
    .string()
    .required("Este campo es obligatorio")
    .max(500, "Las causas no pueden exceder los 500 caracteres"),
  consequences: yup
    .string()
    .required("Este campo es obligatorio")
    .max(500, "Las consecuencias no pueden exceder los 500 caracteres"),
  impact: yup.string().required("Este campo es obligatorio"),
  probability: yup.string().required("Este campo es obligatorio"),
  vulnerability: yup.string().required("Este campo es obligatorio"),
  controlType: yup.string().required("Este campo es obligatorio"),
  controlFrequency: yup.string().required("Este campo es obligatorio")
});
const columnsHelp = [
  { field: "name", header: "Tipo de riesgo" },
  {
    field: "description",
    header: "Definición del tipo de riesgo",
    columnType: "fullText"
  }
];
const controlEffectivenessLevel = (levelItem) => {
  switch (levelItem) {
    case 1:
      return {
        class: "extreme-level",
        textClass: "extreme-level-text",
        text: "Inexsistente"
      };
    case 2:
    case 3:
    case 4:
      return {
        class: "low-level",
        textClass: "low-level-text",
        text: "Bajo"
      };
    case 5:
    case 6:
    case 7:
    case 8:
      return {
        class: "medium-level",
        textClass: "medium-level-text",
        text: "Medio"
      };
    case 9:
    case 10:
    case 11:
    case 12:
      return {
        class: "high-level",
        textClass: "high-level-text",
        text: "Alto"
      };
    default:
      return {
        class: "default-level",
        textClass: "default-level-text",
        text: "Sin Nivel"
      };
  }
};

const residualRiskLevel = (levelItem) => {
  if (!levelItem || levelItem === Infinity) {
    return {
      class: "default-level",
      textClass: "default-level-text",
      text: "Sin Nivel"
    };
  }
  // valor < 5 tolerable
  // valor <= 7 moderado
  // valor >= 8 importante
  // valor >= 12 inaceptable
  switch (true) {
    case levelItem < 5:
      return {
        class: "low-level",
        textClass: "low-level-text",
        text: "Tolerable"
      };
    case levelItem <= 7:
      return {
        class: "medium-level",
        textClass: "medium-level-text",
        text: "Moderado"
      };
    case levelItem >= 8 && levelItem < 12:
      return {
        class: "high-level",
        textClass: "high-level-text",
        text: "Importante"
      };
    case levelItem >= 12:
      return {
        class: "high-level",
        textClass: "high-level-text",
        text: "Nivel Alto"
      };
    default:
      return {
        class: "default-level",
        textClass: "default-level-text",
        text: "Sin Nivel"
      };
  }
};

const ControlEffectivenessComponent = ({ levelItem }) => {
  const {
    class: levelClass,
    textClass,
    text
  } = controlEffectivenessLevel(levelItem);

  return (
    <div className={`level ${levelClass}`}>
      <p className={textClass}>{text}</p>
    </div>
  );
};

const ResidualRiskComponent = ({ levelItem }) => {
  const { class: levelClass, textClass, text } = residualRiskLevel(levelItem);

  return (
    <div className={`level ${levelClass}`}>
      <p className={textClass}>{text}</p>
    </div>
  );
};

const CreateRisk = () => {
  const location = useLocation();
  const userSesion = getCookie("sesion");
  const userData = useSelector((state) => state.userData.userData);
  const [openModal, setOpenModal] = useState(false);
  const [openHelpModal, setOpenHelpModal] = useState(false);
  const [riskType, setRiskType] = useState([]);
  const [process, setProcess] = useState([]);
  const [helpId, setHelpId] = useState(0);

  const [strategies, setStrategies] = useState([]);

  const [riskLevel, setRiskLevel] = useState({ level: 0, type: "default" });
  const [controlEffectiveness, setControlEffectiveness] = useState(0);
  const [residualRisk, setResidualRisk] = useState(0);
  const [residualRiskType, setResidualRiskType] = useState(0);
  const [riskLeveltype, setRiskLeveltype] = useState(0);

  const navigate = useNavigate();
  const selectedPeriod = location?.state ?? {};
  const showPeriodAlert =
    selectedPeriod.year &&
    selectedPeriod.month &&
    !getCurrentMonthYear(selectedPeriod.year, selectedPeriod.month);
  const selectedPeriodText = `${getMonthName(Number(selectedPeriod.month))} ${
    selectedPeriod.year
  }`;

  const LevelComponent = ({ levelItem }) => {
    const { class: levelClass, textClass, text } = getRiskLevel(levelItem);

    return (
      <div className={`level ${levelClass}`}>
        <p className={textClass}>{text}</p>
      </div>
    );
  };

  const getRiskLevel = (levelItem) => {
    const result = getRiskLevelFromScore(levelItem.level);
    setRiskLeveltype(result.riskLeveltype);
    return { class: result.class, textClass: result.textClass, text: result.text };
  };

  const createRisk = async (
    formData,
    riskLevel,
    controlEffectiveness,
    residualRisk,
    navigate
  ) => {
    const objRequest = {
      userId: userSesion.id_usuario,
      roleList: userData?.roleList ?? [],
      processId: formData.impactProcess,
      description: formData.description,
      title: formData.riskTitle,
      riskTypeId: formData.riskType,
      causes: formData.causes,
      consecuenses: formData.consequences,
      impact: formData.impact,
      probability: formData.probability,
      vulnerability: formData.vulnerability,
      riskLevel: riskLevel,
      typeControl: formData.controlType,
      frecuencyControl: formData.controlFrequency,
      eficiencyControl: controlEffectiveness,
      residualRisk: residualRisk,
      strategies: formData.strategies,
      year: selectedPeriod.year,
      month: selectedPeriod.month
    };

    callApiAsync(
      () => postCreateRisk(objRequest),
      (data) => {
        const riskId = data.riskMonthId;
        navigate("/riskAndOpportunityManagement/riskAdminGestion/riskDetails", {
          state: {
            rowData: {
              rmId: riskId
            }
          }
        });
      }
    );
  };

  const {
    control,
    handleSubmit,
    getValues,
    watch,
    formState: { errors, isValid }
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: "all"
  });

  const watchImpactProcess = watch("impactProcess");
  const watchStrategy = watch("strategies");

  const onSubmit = (formData) => {
    createRisk(
      formData,
      riskLeveltype,
      controlEffectiveness,
      residualRiskType,
      navigate
    );
  };

  const handleClose = () => {
    setOpenModal(false);
    setOpenHelpModal(false);
  };

  const calculateRiskLevel = useCallback(() => {
    const formData = getValues();
    const probability = parseInt(formData.probability, 10);
    const impact = parseInt(formData.impact, 10);
    const vulnerability = parseInt(formData.vulnerability, 10);

    if (probability && impact && vulnerability) {
      const level = getRiskScore(impact, vulnerability, probability);
      setRiskLevel({ level });
      const result = getRiskLevelFromScore(level);
      setRiskLeveltype(result.riskLeveltype);
    }
  }, [getValues, setRiskLevel, setRiskLeveltype]);

  useEffect(() => {
    calculateRiskLevel();
  }, [calculateRiskLevel]);

  const calculateControlEffectiveness = useCallback(() => {
    const formData = getValues();
    const controlType = parseInt(formData.controlType, 10);
    const controlFrequency = parseInt(formData.controlFrequency, 10);

    if (controlType && controlFrequency) {
      const level = controlType * controlFrequency;
      setControlEffectiveness(level);
    }
  }, [getValues, setControlEffectiveness]);

  useEffect(() => {
    calculateControlEffectiveness();
  }, [calculateControlEffectiveness]);

  const calculateResidualRisk = useCallback(() => {
    const level = riskLevel.level / controlEffectiveness;
    let type = 0;
    if (level < 5) {
      type = 1;
    } else if (level <= 7) {
      type = 2;
    } else if (level >= 8 && level < 12) {
      type = 3;
    } else if (level >= 12) {
      type = 4;
    } else {
      type = 0;
    }
    setResidualRisk(level);
    setResidualRiskType(type);
  }, [riskLevel, controlEffectiveness]);

  useEffect(() => {
    calculateResidualRisk();
  }, [calculateResidualRisk]);

  const getRiskType = async () => {
    callApiAsync(getCallRiskType, (data) => {
      setRiskType(data);
    });
  };

  const getProcess = async () => {
    callApiAsync(getCallProcess, (data) => {
      var dataFiltered = filterProcessByRole(userData.roleList, data);
      setProcess(dataFiltered);
    });
  };

  const getStrategiesSelectData = async () => {
    if (!watchImpactProcess) {
      setStrategies([]);
      return;
    }
    const now = new Date();
    const year = selectedPeriod.year ?? now.getFullYear();
    const month = selectedPeriod.month ?? now.getMonth() + 1;
    callApiAsync(
      () => getStrategiesSelect(watchImpactProcess, year, month),
      (data) => {
        setStrategies(data);
      }
    );
  };

  useEffect(() => {
    getRiskType();
    getProcess();
  }, []);

  useEffect(() => {
    getStrategiesSelectData();
  }, [watchImpactProcess]);

  return (
    <div>
      <BreadCrumbComp />
      <HeaderBanner type={headerBannerType.riskAndOportunity} />
      <div className="searchTitle riskAndOpportunityManagement-title">
        <h1>CREAR NUEVO RIESGO</h1>
      </div>
      {showPeriodAlert && (
        <Alert severity="warning" className="mb-20">
          Se va a crear este riesgo en el periodo {selectedPeriodText}.
        </Alert>
      )}
      <div className="create-form-container">
        <p className="createRist-title ml-0"> Riesgo</p>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container justifyContent="center" spacing={2}>
            <Grid item xs={12} md={6}>
              <div className="IHContainer align-center">
                <p className="create-label ml-0">Tipo de Riesgo:</p>
                <div
                  className="pointer align-center j-center ml-5"
                  onClick={() => setOpenModal(true)}
                >
                  <img
                    className="btn-add-icon"
                    src={icons.infoExclamation}
                    alt="icon"
                    style={{ margin: "0 0 7px 0", width: "30px" }}
                  />
                </div>
              </div>
              <div>
                <SimpleSelect
                  name="riskType"
                  control={control}
                  options={riskType} //
                  error={errors.riskType}
                  hideLabel={true}
                  margin={"0"}
                />
              </div>
            </Grid>
            <Grid item xs={12} md={6}>
              <p className="create-label ml-0">Título del Riesgo:</p>
              <FormTextField
                name="riskTitle"
                control={control}
                error={errors.riskTitle}
                margin={"0"}
              />
            </Grid>

            <Grid item xs={12} md={6}></Grid>

            <Grid item xs={12}>
              <p className="create-label ml-0">Descripción:</p>
              <FormTextField
                name="description"
                control={control}
                isTextArea={true}
                error={errors.description}
                maxLength={1500}
                margin={"0"}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <p className="create-label ml-0">Proceso de Impacto:</p>
              <SimpleSelect
                name="impactProcess"
                control={control}
                options={process}
                error={errors.impactProcess}
                hideLabel={true}
                margin={"0"}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <div className="IHContainer align-center">
                <p className="create-label ml-0">Relacionar con estrategia:</p>
              </div>
              <div>
                <SimpleSelect
                  name="strategies"
                  control={control}
                  options={strategies}
                  error={errors.riskType}
                  hideLabel={true}
                  margin={"0"}
                />
              </div>
            </Grid>
            {watchStrategy && (
              <Grid item xs={12}>
                <StrategyRelationsTable strategyId={watchStrategy} />
              </Grid>
            )}

            <Grid item xs={12}>
              <p className="create-label ml-0">Causas:</p>
              <FormTextField
                name="causes"
                control={control}
                isTextArea={true}
                error={errors.causes}
                maxLength={500}
                margin={"0"}
              />
            </Grid>
            <Grid item xs={12}>
              <p className="create-label ml-0">Consecuencias:</p>
              <FormTextField
                name="consequences"
                control={control}
                isTextArea={true}
                error={errors.consequences}
                maxLength={500}
                margin={"0"}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              {/* <p className="create-label ml-0">Impacto:</p> */}
              <div className="IHContainer align-center">
                <p className="create-label ml-0">Impacto:</p>
                <div
                  className="pointer align-center j-center ml-5"
                  onClick={() => {
                    setHelpId(riskHelpIds.IMPACT);
                    setOpenHelpModal(true);
                  }}
                >
                  <img
                    className="btn-add-icon"
                    src={icons.infoExclamation}
                    alt="icon"
                    style={{ margin: "0 0 7px 0", width: "30px" }}
                  />
                </div>
              </div>
              <SimpleSelect
                name="impact"
                control={control}
                options={impactData}
                error={errors.impact}
                onChange={calculateRiskLevel}
                hideLabel={true}
                margin={"0"}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <div className="IHContainer align-center">
                <p className="create-label ml-0">Vulnerabilidad:</p>
                <div
                  className="pointer align-center j-center ml-5"
                  onClick={() => {
                    setHelpId(riskHelpIds.VULNERABILITY);
                    setOpenHelpModal(true);
                  }}
                >
                  <img
                    className="btn-add-icon"
                    src={icons.infoExclamation}
                    alt="icon"
                    style={{ margin: "0 0 7px 0", width: "30px" }}
                  />
                </div>
              </div>
              <SimpleSelect
                name="vulnerability"
                control={control}
                options={vulnerabilityData}
                error={errors.vulnerability}
                onChange={calculateRiskLevel}
                hideLabel={true}
                margin={"0"}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              {/* <p className="create-label ml-0">Probabilidad:</p> */}
              <div className="IHContainer align-center">
                <p className="create-label ml-0">Probabilidad:</p>
                <div
                  className="pointer align-center j-center ml-5"
                  onClick={() => {
                    setHelpId(riskHelpIds.PROBABILITY);
                    setOpenHelpModal(true);
                  }}
                >
                  <img
                    className="btn-add-icon"
                    src={icons.infoExclamation}
                    alt="icon"
                    style={{ margin: "0 0 7px 0", width: "30px" }}
                  />
                </div>
              </div>
              <SimpleSelect
                name="probability"
                control={control}
                options={probabilityData}
                error={errors.probability}
                onChange={calculateRiskLevel}
                hideLabel={true}
                margin={"0"}
              />
            </Grid>

            <Grid
              item
              xs={12}
              className={`${riskLevel.level !== 0 ? "" : "hide"}`}
            >
              <div
                className={`IHContainer align-center pillcontainer ${
                  riskLevel.level !== 0 ? "" : "hide"
                }`}
              >
                {/* <p className="create-label">Nivel de Riesgo:</p> */}

                <div className="IHContainer align-center">
                  <p className="create-label ml-0">Nivel de Riesgo:</p>
                  <div
                    className="pointer align-center j-center ml-5"
                    onClick={() => {
                      setHelpId(riskHelpIds.RISKLEVEL);
                      setOpenHelpModal(true);
                    }}
                  >
                    <img
                      className="btn-add-icon"
                      src={icons.infoExclamation}
                      alt="icon"
                      style={{ margin: "0 0 7px 0", width: "30px" }}
                    />
                  </div>
                </div>

                <LevelComponent levelItem={riskLevel} />
              </div>
            </Grid>

            <Grid
              item
              xs={12}
              className={`${riskLevel.level !== 0 ? "" : "hide"}`}
            >
              {/* <p className="create-label">Tipo de Control:</p> */}

              <div className="IHContainer align-center">
                <p className="create-label ml-0">Tipo de Control:</p>
                <div
                  className="pointer align-center j-center ml-5"
                  onClick={() => {
                    setHelpId(riskHelpIds.CONTROLTYPE);
                    setOpenHelpModal(true);
                  }}
                >
                  <img
                    className="btn-add-icon"
                    src={icons.infoExclamation}
                    alt="icon"
                    style={{ margin: "0 0 7px 0", width: "30px" }}
                  />
                </div>
              </div>

              <SimpleSelect
                name="controlType"
                control={control}
                options={controlType}
                error={errors.controlType}
                onChange={calculateControlEffectiveness}
                hideLabel={true}
              />
            </Grid>

            <Grid
              item
              xs={12}
              className={`${riskLevel.level !== 0 ? "" : "hide"}`}
            >
              {/* <p className="create-label">Frecuencia del Control:</p> */}
              <div className="IHContainer align-center">
                <p className="create-label ml-0">Frecuencia del Control:</p>
                <div
                  className="pointer align-center j-center ml-5"
                  onClick={() => {
                    setHelpId(riskHelpIds.FREQUENCY);
                    setOpenHelpModal(true);
                  }}
                >
                  <img
                    className="btn-add-icon"
                    src={icons.infoExclamation}
                    alt="icon"
                    style={{ margin: "0 0 7px 0", width: "30px" }}
                  />
                </div>
              </div>
              <SimpleSelect
                name="controlFrequency"
                control={control}
                options={controlFrequency}
                error={errors.controlFrequency}
                onChange={calculateControlEffectiveness}
                hideLabel={true}
              />
            </Grid>

            <Grid
              item
              xs={12}
              className={`${riskLevel.level !== 0 ? "" : "hide"} ${
                controlEffectiveness !== 0 ? "" : "hide"
              }`}
            >
              <div className="IHContainer align-center pillcontainer">
                {/* <p className="create-label">Eficacia de Control:</p> */}
                <div className="IHContainer align-center">
                  <p className="create-label ml-0">Eficacia de Control:</p>
                  <div
                    className="pointer align-center j-center ml-5"
                    onClick={() => {
                      setHelpId(riskHelpIds.CONTROLEFFECTIVENESS);
                      setOpenHelpModal(true);
                    }}
                  >
                    <img
                      className="btn-add-icon"
                      src={icons.infoExclamation}
                      alt="icon"
                      style={{ margin: "0 0 7px 0", width: "30px" }}
                    />
                  </div>
                </div>
                <ControlEffectivenessComponent
                  levelItem={controlEffectiveness}
                />
              </div>
            </Grid>

            <Grid
              item
              xs={12}
              className={`${riskLevel.level !== 0 ? "" : "hide"} ${
                controlEffectiveness !== 0 ? "" : "hide"
              }`}
            >
              <div className="IHContainer align-center pillcontainer">
                <div className="IHContainer align-center">
                  <p className="create-label ml-0">Riesgo residual:</p>
                  {/* <p className="create-label ml-0">Frecuencia del Control:</p> */}
                  <div
                    className="pointer align-center j-center ml-5"
                    onClick={() => {
                      setHelpId(riskHelpIds.RESIDUALRISK);
                      setOpenHelpModal(true);
                    }}
                  >
                    <img
                      className="btn-add-icon"
                      src={icons.infoExclamation}
                      alt="icon"
                      style={{ margin: "0 0 7px 0", width: "30px" }}
                    />
                  </div>
                </div>
                <ResidualRiskComponent
                  levelItem={residualRisk} //// dividiendo nivel del riesgo/eficacia del control   valor  si
                  // valor < 5 tolerable
                  // valor <= 7 moderado
                  // valor >= 8 importante moderado
                  // valor >= 12 inaceptable
                />
              </div>
            </Grid>
          </Grid>
          <div className="j-center white-space">
            <LaddaButton
              width={"160px"}
              fontSize={"12px"}
              height={"40px"}
              variant="contained"
              type="submit"
              disabled={!isValid}
            >
              <p className="button_text">Crear Riesgo</p>
            </LaddaButton>
          </div>
        </form>
      </div>
      <ModalComponentRiskHelp
        openModal={openHelpModal}
        handleClose={handleClose}
        helpId={helpId}
      />
      <ModalComponent
        open={openModal}
        handleClose={handleClose}
        title={"Tipo de Riesgo"}
      >
        <SimpleTable data={riskType} columns={columnsHelp} />
      </ModalComponent>
    </div>
  );
};

export default CreateRisk;
